import { FC, Fragment, useState } from 'react';
import { useApp } from '@/App';
import FleetTitle from '@/libs/antd/components/Title';
import { getCustomers } from '@/libs/fleet-api/services/customer';
import { useQuery } from '@tanstack/react-query';
import {
	Button,
	Col,
	Flex,
	Form,
	Input,
	Radio,
	Row,
	Select,
	Space,
	Table,
} from 'antd';
import { EditOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { FleetPagination, FleetQueryParams } from '@/libs/fleet-api/fetcher';
import { DUMMY_EXISTING_PLAFON } from '@/libs/fleet-api/dummy';
import { useModals } from '@/components/modals/ModalProvider';
import dayjs from 'dayjs';
import { useUserData } from '@/libs/fleet-api/hooks';

const CustomerListPage: FC = () => {
	const { navigate } = useApp();
	const userData = useUserData();
	const [myCustomer, setMyCustomer] = useState<boolean>(false);
	const [searchBy, setSearchBy] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState<string | null>(null);
	const [pagination, setPagination] = useState<FleetPagination>({
		pageNumber: 1,
		pageSize: 20,
	});
	const [queryParams, setQueryParams] = useState<FleetQueryParams>({
		search: undefined,
		filter: undefined,
		pagination,
		myCustomer,
	});

	const { data, isLoading, isFetching } = useQuery({
		queryKey: ['getCustomers', queryParams] as const,
		queryFn: ({ queryKey }) => {
			const [, queryParams] = queryKey;
			return getCustomers(queryParams);
		},
	});

	const dataSource = data?.data.items ?? [];
	const { openCustomerDetail } = useModals();

	const handleSearch = () => {
		setQueryParams((current) => {
			if (!searchBy || !searchValue) {
				return {
					...current,
					search: undefined,
				};
			}
			return {
				...current,
				search: {
					by: searchBy,
					value: searchValue,
				},
			};
		});
	};

	return (
		<Fragment>
			<Space
				direction="vertical"
				style={{ display: 'flex' }}
				size={'large'}
			>
				<FleetTitle>Customer Information</FleetTitle>
				<Flex justify="end">
					<Radio.Group
						options={[
							{ label: 'My Customer', value: 'my' },
							{ label: 'All Customer', value: 'all' },
						]}
						onChange={(e) => {
							setMyCustomer(e.target.value === 'my');
							if (e.target.value === 'my') {
								setPagination(() => {
									return {
										pageSize: 20,
										pageNumber: 1,
									};
								});
								setQueryParams((current) => {
									return {
										...current,
										myCustomer: e.target.value === 'my',
										pagination: {
											pageSize: current.pagination?.pageSize ?? 20,
											pageNumber: 1,
										},
									};
								});
							}
							setQueryParams((current) => {
								return {
									...current,
									myCustomer: e.target.value === 'my',
								};
							});
						}}
						value={myCustomer ? 'my' : 'all'}
						optionType="button"
						buttonStyle="solid"
					/>
				</Flex>
				<Form
					layout="horizontal"
					colon={false}
					labelAlign="left"
					onFinish={handleSearch}
				>
					<Row gutter={[20, 20]}>
						<Col flex="auto">
							<Form.Item
								label={<span>Search By</span>}
								labelCol={{ flex: 'auto' }}
								wrapperCol={{ flex: 'none', style: { width: 250 } }}
							>
								<Select
									placeholder="Search By"
									options={[
										{
											label: 'Nama Customer',
											value: 'customer-name',
										},
										{
											label: 'NPWP',
											value: 'npwp',
										},
									]}
									allowClear
									onChange={(value) => {
										if (value === 'my-customer') {
											setSearchValue(null);
										}
										setSearchBy(value);
									}}
									onClear={() => {
										setQueryParams((current) => {
											return {
												...current,
												search: undefined,
											};
										});
										setSearchBy(null);
									}}
									style={{
										display: 'block',
										width: '100%',
									}}
								/>
							</Form.Item>
						</Col>
						<Col flex="auto">
							<Form.Item>
								<Input
									placeholder="Search..."
									style={{
										display: 'block',
									}}
									disabled={searchBy === null || searchBy === 'my-customer'}
									value={searchValue!}
									onChange={(e) => {
										const value = e.target.value;
										setSearchValue(value);
									}}
								/>
							</Form.Item>
						</Col>
						<Col>
							<Form.Item>
								<Button
									block
									type="primary"
									htmlType="submit"
								>
									SEARCH
								</Button>
							</Form.Item>
						</Col>
					</Row>
				</Form>
				<Table
					scroll={{ x: 'max-content' }}
					bordered
					loading={isLoading || isFetching}
					dataSource={dataSource.map((data, key) => {
						return { ...data, key };
					})}
					columns={[
						{
							title: 'Edit',
							width: 1,
							align: 'center',
							render: (_, record) => {
								// allow edit estar customer first.
								if (
									(userData?.fullName !== record?.createdBy &&
										(record.source === 'LOS' ||
											record.source === 'ESTARLOS')) ||
									record.status === 'WAITING' ||
									record.status === 'REJECT'
								) {
									return <EditOutlined style={{ color: '#bababa' }} />;
								}

								return (
									<a
										style={{ display: 'block' }}
										onClick={(e) => {
											e.preventDefault();
											navigate('CustomerPage', {
												customerId: record.customerId,
											});
										}}
									>
										<EditOutlined />
									</a>
								);
							},
						},
						{
							title: 'Customer ID',
							dataIndex: ['customerId'],
						},
						{
							title: 'Nama Customer',
							dataIndex: ['customerName'],
							render: (_, record) => (
								<a
									onClick={(e) => {
										e.preventDefault();
										openCustomerDetail(record.customerId);
									}}
								>
									{record.customerName}
								</a>
							),
						},
						{
							title: 'Sumber',
							align: 'center',
							render: (_, record) =>
								record.source === 'ESTARLOS' ? 'ESTAR' : record.source,
							// dataIndex: ['source'],
						},
						{
							title: 'Status Customer',
							width: 1,
							align: 'center',
							dataIndex: ['status'],
						},
						{
							title: 'Group Customer BMPP',
							dataIndex: ['customerGroup'],
							render: (_, record) => record.customerGroup ?? '-',
						},
						{
							title: 'Nama FBDH',
							render: (record) => record.reportTo ?? '-',
						},
						{
							title: 'Nama FRM',
							dataIndex: ['createdBy'],
						},
						{
							title: 'Masa Berlaku Customer ID',
							align: 'center',
							width: 1,
							render: (record) => {
								if (!record.expiredAt) {
									return '-';
								}
								const diff = dayjs(record.expiredAt).diff(dayjs(), 'day');
								if (diff < 1) {
									return 'Expired';
								}
								return diff + ' hari';
							},
						},
						{
							title: 'Status Prospect Aktif',
							align: 'center',
							width: 1,
							render: (record) => {
								if (!record.prospects) {
									return '-';
								}
								const activeProspect =
									record.prospects[record.prospects.length - 1];
								return activeProspect.prospectStatus.toUpperCase();
							},
						},
						{
							title: 'Pending User',
							render: (record) => {
								if (!record.prospects) {
									return record.status === 'WAITING'
										? 'Fleet Solution'
										: '-';
								}
								const activeProspect =
									record.prospects[record.prospects.length - 1];
								return activeProspect.pendingUser ?? '-';
							},
						},
						{
							title: 'Prospect (temp)',
							width: 1,
							align: 'center',
							render: (_, record, index) => {
								let hasExpired = false;
								if (!record.expiredAt) {
									hasExpired = true;
								} else {
									const diff = dayjs(record.expiredAt).diff(dayjs(), 'day');
									hasExpired = diff < 1;
								}
								const hasActiveProspect =
									record.prospects?.length ?? 0 > 0 ? true : false;
								const lastProspectNotReject = record.prospects?.length
									? record.prospects[record.prospects.length - 1].prospectStatus
											.toLowerCase()
											.indexOf('rej') === -1
									: true;
								if (
									((hasActiveProspect ||
										hasExpired ||
										record.status !== 'ACTIVE') &&
										lastProspectNotReject) ||
									userData?.fullName !== record?.createdBy
								) {
									return <ArrowRightOutlined style={{ color: '#bababa' }} />;
								}
								return (
									<a
										style={{ display: 'block' }}
										onClick={(e) => {
											e.preventDefault();
											navigate('FRMProspectPage', {
												customerId: record.customerId!,
												index: index % DUMMY_EXISTING_PLAFON.length,
											});
										}}
									>
										<ArrowRightOutlined />
									</a>
								);
							},
						},
					]}
					pagination={{
						total: data?.data.totalCount,
						current: pagination.pageNumber,
						pageSize: pagination.pageSize,
						onChange: (pageNumber, pageSize) => {
							setPagination((current) => {
								return {
									pageSize,
									pageNumber: current.pageSize !== pageSize ? 1 : pageNumber,
								};
							});
							setQueryParams((current) => {
								return {
									...current,
									pagination: {
										pageSize,
										pageNumber:
											current.pagination?.pageSize !== pageSize
												? 1
												: pageNumber,
									},
								};
							});
						},
					}}
				/>
				<Button
					type="primary"
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
					}}
					onClick={() => navigate('CustomerPage')}
				>
					ADD
				</Button>
			</Space>
		</Fragment>
	);
};

export default CustomerListPage;
